import { useEffect } from "react";
import { useProduct } from "@/features/product/product-context";
import { useMarketPrice } from "@/backend/store";
import { useMarket } from "@/domains/markets";
import { formatCurrency } from "@/lib/utils";

/**
 * Get display name for an asset from market data.
 * Falls back to the raw symbol code if displayName is not set.
 */
const getAssetName = (displayName: string | null | undefined, baseCode: string): string => {
  return displayName || baseCode;
};

/**
 * Generate SEO description for a market using the new branded template.
 */
const getMarketDescription = (displayName: string, baseCode: string): string => {
  return `Trade ${displayName} (${baseCode}) perpetual futures on Nordex. Lightning-fast execution, non-custodial & secure. Powered by Performance Lab Pro\u2122 analytics.`;
};

/**
 * Custom hook that updates SEO meta tags for market pages.
 * 
 * - Updates document title with current price
 * - Updates meta description for the market
 * - Adds structured data (JSON-LD) for rich search results
 * 
 * Uses dynamic display names from the markets API (set via admin panel)
 * instead of hardcoded asset name maps.
 */
export const useMarketSEO = () => {
  const { product } = useProduct();
  const marketPrice = useMarketPrice(product?.id || "BTC-USD");
  const market = useMarket(product?.id);

  // Update document title with price
  useEffect(() => {
    if (!product || marketPrice === 0) {
      document.title = "Nordex - Leading Crypto Futures Exchange | Lab Pro\u2122";
      return;
    }

    const baseSymbol = product.base.code;
    const assetName = getAssetName(market?.displayName, baseSymbol);
    const formattedPrice = formatCurrency(marketPrice, product.quote.precision);
    
    // SEO-optimized title: "BTC $45,234.56 | Trade Bitcoin Perpetuals - Nordex"
    document.title = `${baseSymbol} $${formattedPrice} | Trade ${assetName} Perpetuals - Nordex`;
  }, [marketPrice, product, market?.displayName]);

  // Update meta description, canonical URL, and og:url for SEO
  useEffect(() => {
    if (!product) return;

    const baseCode = product.base.code;
    const assetName = getAssetName(market?.displayName, baseCode);
    const description = getMarketDescription(assetName, baseCode);
    const tradeUrl = `https://app.nordex.trade/trade/${product.id}`;
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Update Open Graph description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }

    // Update Twitter description
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }

    // Update canonical URL to the specific trade page
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', tradeUrl);

    // Update og:url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', tradeUrl);
    }

    // Cleanup: reset canonical and og:url when leaving the trade page
    return () => {
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) {
        canonicalEl.setAttribute('href', 'https://app.nordex.trade/');
      }
      const ogUrlEl = document.querySelector('meta[property="og:url"]');
      if (ogUrlEl) {
        ogUrlEl.setAttribute('content', 'https://app.nordex.trade/');
      }
    };
  }, [product, market?.displayName]);

  // Add structured data (JSON-LD) for rich search results
  useEffect(() => {
    if (!product) return;

    const baseCode = product.base.code;
    const assetName = getAssetName(market?.displayName, baseCode);
    const frontendUrl = import.meta.env.VITE_FRONTEND_URL || "https://nordex.trade";
    
    // Create structured data for this trading page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": `Trade ${assetName} (${baseCode}) Perpetual Futures - Nordex`,
      "description": getMarketDescription(assetName, baseCode),
      "url": `${frontendUrl}/trade/${product.id}`,
      "mainEntity": {
        "@type": "FinancialProduct",
        "name": `${baseCode}-USD Perpetual Futures`,
        "description": `Trade ${assetName} perpetual futures contracts with leverage on Nordex`,
        "provider": {
          "@type": "Organization",
          "name": "Nordex",
          "url": frontendUrl
        },
        "category": "Cryptocurrency Derivatives"
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Nordex",
            "item": frontendUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Trade",
            "item": `${frontendUrl}/trade`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${baseCode}-USD`,
            "item": `${frontendUrl}/trade/${product.id}`
          }
        ]
      }
    };

    // Remove existing structured data if any
    const existingScript = document.querySelector('script[data-market-seo="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-market-seo', 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup on unmount or product change
    return () => {
      const scriptToRemove = document.querySelector('script[data-market-seo="true"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [product, market?.displayName]);

  // Reset on unmount
  useEffect(() => {
    return () => {
      document.title = "Nordex - Leading Crypto Futures Exchange | Lab Pro\u2122";
    };
  }, []);
};
